import { TextAnimator } from './TextAnimator';
import type { AnimatedTextInstance, AnimatedTextOptions } from './types';

/**
 * AnimatedTextManager — tracks live animated text instances and provides
 * convenience wrappers for common game beats (announcements, damage numbers,
 * ability names, counters, etc.).
 *
 * Builds on TextAnimator. The manager is optional; consumers can call
 * TextAnimator directly. This manager adds bookkeeping and a small DSL for
 * the most common H2000 use cases.
 */
export class AnimatedTextManager {
  private animator: TextAnimator;
  private instances: AnimatedTextInstance[] = [];

  constructor(scene: unknown) {
    this.animator = new TextAnimator(scene);
  }

  public setScene(scene: unknown): void {
    this.animator.setScene(scene);
  }

  /**
   * Show any custom animated text.
   */
  public show(opts: AnimatedTextOptions): AnimatedTextInstance {
    const instance = this.animator.animate(opts);
    this.instances.push(instance);
    this.compact();
    return instance;
  }

  /**
   * Dramatic screen-space announcement (e.g. "VICTORY", "YOU DIED").
   */
  public announce(
    text: string,
    x: number,
    y: number,
    overrides: Partial<AnimatedTextOptions> = {},
  ): AnimatedTextInstance {
    return this.show({
      text,
      x,
      y,
      style: {
        fontSize: 32,
        color: 0xffdd44,
        fontWeight: 'bold',
        strokeWidth: 4,
        anchor: { x: 0.5, y: 0.5 },
        screenSpace: true,
        ...overrides.style,
      },
      animation: ['pop', 'pulse', 'float'],
      durationMs: 2500,
      holdMs: 800,
      autoDestroy: true,
      ...overrides,
    });
  }

  /**
   * Combat damage number with impact feel.
   */
  public damage(
    amount: number,
    x: number,
    y: number,
    isCritical: boolean = false,
  ): AnimatedTextInstance {
    const color = isCritical ? 0xffaa00 : 0xff0000;
    const fontSize = isCritical ? 28 : 24;
    const animation: AnimatedTextOptions['animation'] = ['pop', 'shake', 'float'];
    return this.show({
      text: amount.toString(),
      x,
      y: y - 20,
      style: { fontSize, color, fontWeight: 'bold', strokeWidth: 3 },
      animation,
      durationMs: 1000,
      holdMs: 0,
      popScale: isCritical ? 1.6 : 1.3,
      shakeIntensity: isCritical ? 6 : 3,
      floatDistance: 60,
    });
  }

  /**
   * Healing number (green, gentle bounce).
   */
  public heal(amount: number, x: number, y: number): AnimatedTextInstance {
    return this.show({
      text: `+${amount}`,
      x,
      y: y - 20,
      style: { fontSize: 22, color: 0x00ff00, fontWeight: 'bold', strokeWidth: 3 },
      animation: ['pop', 'wave', 'float'],
      durationMs: 1000,
      holdMs: 0,
      popScale: 1.2,
      waveAmplitude: 4,
      floatDistance: 50,
    });
  }

  /**
   * Ability name cast label (typewriter + fade).
   */
  public abilityName(abilityName: string, x: number, y: number): AnimatedTextInstance {
    return this.show({
      text: abilityName,
      x,
      y: y - 40,
      style: { fontSize: 18, color: 0xaa00ff, fontWeight: 'bold', strokeWidth: 2 },
      animation: ['fadeIn', 'typewriter', 'float'],
      durationMs: 1500,
      holdMs: 300,
      floatDistance: 40,
    });
  }

  /**
   * XP / resource gain counter.
   */
  public counter(
    value: number,
    x: number,
    y: number,
    label: string = 'XP',
    overrides: Partial<AnimatedTextOptions> = {},
  ): AnimatedTextInstance {
    return this.show({
      text: `${value}`,
      x,
      y: y - 30,
      style: { fontSize: 20, color: 0x3498db, fontWeight: 'bold', strokeWidth: 2 },
      animation: ['count', 'pop', 'float'],
      durationMs: 1200,
      holdMs: 0,
      countFrom: 0,
      countTo: value,
      countSuffix: ` ${label}`,
      ...overrides,
    });
  }

  /**
   * Remove completed instances from the tracking list.
   */
  private compact(): void {
    this.instances = this.instances.filter((i) => i.isAlive());
  }

  /**
   * Clean up all tracked instances.
   */
  public cleanup(): void {
    this.instances.forEach((i) => i.destroy());
    this.instances = [];
  }
}
